import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import EnglishDatePicker from '../components/EnglishDatePicker/EnglishDatePicker';
import Sidebar from '../components/Sidebar';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://metro4.dcairo.com/api';

const PoursApproved = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pours, setPours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    station: 'all',
    testType: 'all',
    dateFrom: '',
    dateTo: ''
  });
  
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // جلب الصبات المعتمدة
  const fetchApprovedPours = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = {
        status: 'approved',
        station: filters.station === 'all' ? undefined : filters.station,
        testType: filters.testType === 'all' ? undefined : filters.testType,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined
      };
      
      const response = await axios.get(`${API_URL}/pours`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      if (response.data.success) {
        setPours(response.data.data.filter(pour => pour.status === 'approved'));
      }
    } catch (error) {
      console.error('Error fetching approved pours:', error);
      if (error.response?.status === 401) {
        toast.error('انتهت الجلسة، يرجى تسجيل الدخول مرة أخرى');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApprovedPours();
  }, [filters]);

  // تنسيق التاريخ
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB');
  };

  // تنسيق الوقت
  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateForAPI = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // الحصول على اسم المحطة
  const getStationName = (pour) => {
    if (pour.testType === 'Concrete Pour') {
      return pour.station || '-';
    } else {
      return pour.castingStation || '-';
    }
  };

  // حساب عدد المكعبات
  const calculateCubes = (cubes) => {
    if (!cubes) return '-';
    const { groups, cubesPerGroup } = cubes;
    if (groups && cubesPerGroup) {
      return `${groups}G × ${cubesPerGroup} = ${parseInt(groups) * parseInt(cubesPerGroup)}C`;
    }
    return '-';
  };

  return (
    <div className="dashboard-layout">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <div className="dashboard-content">
        <div className="dashboard-header">
          <button className="menu-toggle" onClick={toggleSidebar}>
            ☰
          </button>
          <h1>✅ Approved Pours</h1>
        </div>

        <div className="dashboard-main">
          <style>{`
            .approved-container {
              padding: 20px;
              max-width: 100%;
              margin: 0 auto;
            }

            .approved-header {
              background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
              padding: 30px;
              border-radius: 20px;
              box-shadow: 0 8px 30px rgba(0,0,0,0.08);
              margin-bottom: 30px;
              border-top: 6px solid #3b82f6;
              position: relative;
              overflow: hidden;
            }

            .approved-header::before {
              content: '';
              position: absolute;
              top: 0;
              right: 0;
              width: 200px;
              height: 200px;
              background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, transparent 100%);
              border-radius: 50%;
              transform: translate(30%, -30%);
            }

            .header-content {
              position: relative;
              z-index: 1;
            }

            .header-title {
              font-size: 28px;
              font-weight: 900;
              color: #1e293b;
              margin: 0 0 15px 0;
              display: flex;
              align-items: center;
              gap: 15px;
              letter-spacing: -0.5px;
            }

            .header-icon {
              font-size: 36px;
              filter: drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3));
            }

            .header-subtitle {
              font-size: 15px;
              color: #64748b;
              margin: 0 0 25px 0;
              font-weight: 500;
            }

            .filters-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
              gap: 18px;
            }

            .filter-group {
              display: flex;
              flex-direction: column;
              gap: 10px;
            }

            .filter-label {
              font-size: 14px;
              font-weight: 700;
              color: #475569;
              display: flex;
              align-items: center;
              gap: 6px;
            }

            .filter-icon {
              font-size: 16px;
            }

            .filter-select,
            .filter-input {
              padding: 12px 16px;
              border: 2px solid #e2e8f0;
              border-radius: 12px;
              font-size: 14px;
              transition: all 0.3s;
              background: white;
              font-weight: 500;
            }

            .filter-select:focus,
            .filter-input:focus {
              outline: none;
              border-color: #3b82f6;
              box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
              transform: translateY(-2px);
            }

            .filter-select:hover,
            .filter-input:hover {
              border-color: #cbd5e1;
            }

            .stats-card {
              background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
              padding: 25px;
              border-radius: 16px;
              box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
              margin-bottom: 30px;
              color: white;
              display: flex;
              align-items: center;
              justify-content: space-between;
            }

            .stat-content {
              flex: 1;
            }

            .stat-value {
              font-size: 48px;
              font-weight: 900;
              margin: 0 0 8px 0;
              text-shadow: 0 2px 4px rgba(0,0,0,0.2);
            }

            .stat-label {
              font-size: 16px;
              font-weight: 600;
              margin: 0;
              opacity: 0.95;
            }

            .stat-icon {
              font-size: 80px;
              opacity: 0.2;
            }

            .table-card {
              background: white;
              border-radius: 20px;
              box-shadow: 0 8px 30px rgba(0,0,0,0.08);
              overflow: hidden;
              border: 1px solid #f1f5f9;
            }

            .table-header {
              background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
              padding: 20px 25px;
              border-bottom: 3px solid #3b82f6;
            }

            .table-header-title {
              font-size: 20px;
              font-weight: 800;
              color: #1e293b;
              margin: 0;
              display: flex;
              align-items: center;
              gap: 10px;
            }

            .table-wrapper {
              overflow-x: auto;
            }

            .pours-table {
              width: 100%;
              border-collapse: collapse;
            }

            .pours-table thead {
              background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
              color: white;
              position: sticky;
              top: 0;
              z-index: 10;
            }

            .pours-table th {
              padding: 18px 15px;
              text-align: center;
              font-weight: 700;
              font-size: 14px;
              white-space: nowrap;
              text-shadow: 0 1px 2px rgba(0,0,0,0.1);
              letter-spacing: 0.3px;
            }

            .pours-table td {
              padding: 16px 15px;
              border-bottom: 1px solid #f1f5f9;
              font-size: 14px;
              color: #334155;
              font-weight: 500;
              text-align: center;
            }

            .pours-table tbody tr {
              transition: all 0.2s;
              background: white;
            }

            .pours-table tbody tr:nth-child(even) {
              background: #f8fafc;
            }

            .pours-table tbody tr:hover {
              background: #dbeafe;
              transform: scale(1.01);
              box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
            }

            .status-badge {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              padding: 8px 16px;
              border-radius: 25px;
              font-size: 13px;
              font-weight: 700;
              background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
              color: #166534;
              border: 2px solid #22c55e;
              box-shadow: 0 2px 8px rgba(34, 197, 94, 0.2);
            }

            .test-type-badge {
              display: inline-block;
              padding: 6px 12px;
              border-radius: 8px;
              font-size: 12px;
              font-weight: 700;
              border: 2px solid;
            }

            .test-type-concrete {
              background: #dbeafe;
              color: #1e40af;
              border-color: #3b82f6;
            }

            .test-type-grout {
              background: #fef3c7;
              color: #92400e;
              border-color: #f59e0b;
            }

            .test-type-trail {
              background: #fce7f3;
              color: #9f1239;
              border-color: #ec4899;
            }

            .created-by {
              display: flex;
              flex-direction: column;
              gap: 4px;
            }

            .user-name {
              font-weight: 700;
              color: #1e293b;
              font-size: 13px;
            }

            .user-role {
              font-size: 11px;
              color: #64748b;
              font-weight: 600;
            }

            .user-station {
              font-size: 11px;
              color: #94a3b8;
            }

            .empty-state {
              text-align: center;
              padding: 100px 20px;
              color: #94a3b8;
            }

            .empty-icon {
              font-size: 100px;
              margin-bottom: 25px;
              opacity: 0.5;
              filter: grayscale(1);
            }

            .empty-title {
              font-size: 26px;
              font-weight: 900;
              color: #64748b;
              margin: 0 0 12px 0;
            }

            .empty-text {
              font-size: 16px;
              margin: 0;
              font-weight: 500;
            }

            /* Mobile Cards View */
            .mobile-cards {
              display: none;
            }

            @media (max-width: 1024px) {
              .table-wrapper {
                display: none;
              }

              .mobile-cards {
                display: block;
                padding: 15px;
              }

              .pour-card-mobile {
                background: white;
                border-radius: 16px;
                padding: 20px;
                margin-bottom: 15px;
                box-shadow: 0 4px 15px rgba(0,0,0,0.08);
                border-right: 5px solid #3b82f6;
                transition: all 0.3s;
              }

              .pour-card-mobile:hover {
                transform: translateY(-3px);
                box-shadow: 0 8px 25px rgba(59, 130, 246, 0.2);
              }

              .card-row {
                display: flex;
                justify-content: space-between;
                padding: 10px 0;
                border-bottom: 1px solid #f1f5f9;
              }

              .card-row:last-child {
                border-bottom: none;
              }

              .card-label {
                font-weight: 700;
                color: #64748b;
                font-size: 13px;
              }

              .card-value {
                color: #1e293b;
                font-size: 13px;
                font-weight: 600;
                text-align: left;
              }

              .card-header-mobile {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 15px;
                padding-bottom: 15px;
                border-bottom: 2px solid #f1f5f9;
              }

              .card-number {
                font-size: 24px;
                font-weight: 900;
                color: #3b82f6;
              }
            }

            @media (max-width: 768px) {
              .approved-container {
                padding: 15px;
              }

              .approved-header {
                padding: 20px;
                border-radius: 16px;
              }

              .header-title {
                font-size: 22px;
              }

              .header-icon {
                font-size: 28px;
              }

              .header-subtitle {
                font-size: 13px;
              }

              .filters-grid {
                grid-template-columns: 1fr;
                gap: 15px;
              }

              .stats-card {
                flex-direction: column;
                text-align: center;
                gap: 15px;
              }

              .stat-value {
                font-size: 36px;
              }

              .stat-icon {
                font-size: 60px;
              }

              .table-header {
                padding: 15px 20px;
              }

              .table-header-title {
                font-size: 16px;
              }
            }

            @media (max-width: 480px) {
              .header-title {
                font-size: 18px;
                flex-direction: column;
                gap: 10px;
              }

              .stat-value {
                font-size: 32px;
              }

              .pour-card-mobile {
                padding: 15px;
              }

              .card-number {
                font-size: 20px;
              }
            }
          `}</style>

          <div className="approved-container">
            {/* Header & Filters */}
            <div className="approved-header">
              <div className="header-content">
                <h2 className="header-title">
                  <span className="header-icon">✅</span>
                  Approved Pours
                </h2>
                <p className="header-subtitle">
                  All officially approved pours
                </p>
                
                <div className="filters-grid">
                  <div className="filter-group">
                    <label className="filter-label">
                      <span className="filter-icon">📍</span>
                      Station
                    </label>
                    <select
                      className="filter-select"
                      value={filters.station}
                      onChange={(e) => setFilters({...filters, station: e.target.value})}
                    >
                      <option value="all">All Stations</option>
                      <option value="ST13 Giza">ST13 Giza</option>
                      <option value="ST14 Giza Square">ST14 Giza Square</option>
                      <option value="S15 Alrawda">S15 Alrawda</option>
                      <option value="ST16 Almalek Elsaleh">ST16 Almalek Elsaleh</option>
                      <option value="ST17 Cut&Cover">ST17 Cut&Cover</option>
                      <option value="Abo Rawash">Abo Rawash</option>
                      <option value="4TL">4TL</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">
                      <span className="filter-icon">🔬</span>
                      Test Type
                    </label>
                    <select
                      className="filter-select"
                      value={filters.testType}
                      onChange={(e) => setFilters({...filters, testType: e.target.value})}
                    >
                      <option value="all">All Types</option>
                      <option value="Concrete Pour">Concrete Pour</option>
                      <option value="Grout">Grout</option>
                      <option value="T.M Trail">T.M Trail</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">
                      <span className="filter-icon">📅</span>
                      From Date
                    </label>
                    <EnglishDatePicker
                      selected={filters.dateFrom ? new Date(filters.dateFrom) : null}
                      onChange={(date) => setFilters({...filters, dateFrom: formatDateForAPI(date)})}
                      maxDate={filters.dateTo ? new Date(filters.dateTo) : new Date()}
                      placeholderText="From Date"
                    />
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">
                      <span className="filter-icon">📅</span>
                      To Date
                    </label>
                    <EnglishDatePicker
                      selected={filters.dateTo ? new Date(filters.dateTo) : null}
                      onChange={(date) => setFilters({...filters, dateTo: formatDateForAPI(date)})}
                      minDate={filters.dateFrom ? new Date(filters.dateFrom) : null}
                      maxDate={new Date()}
                      placeholderText="To Date"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="stats-card">
              <div className="stat-content">
                <h3 className="stat-value">{pours.length}</h3>
                <p className="stat-label">Approved Pour</p>
              </div>
              <div className="stat-icon">📊</div>
            </div>

            {/* Table */}
            <div className="table-card">
              <div className="table-header">
                <h3 className="table-header-title">
                  <span>📋</span>
                  Approved Pours List
                </h3>
              </div>

              {loading ? (
                <div className="empty-state">
                  <div className="empty-icon">⏳</div>
                  <h3 className="empty-title">Loading...</h3>
                  <p className="empty-text">Fetching data</p>
                </div>
              ) : pours.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🔭</div>
                  <h3 className="empty-title">No Approved Pours</h3>
                  <p className="empty-text">No pours have been approved yet</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="table-wrapper">
                    <table className="pours-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Type</th>
                          <th>Date</th>
                          <th>Station</th>
                          <th>Element</th>
                          <th>Mix Code</th>
                          <th>Quantity</th>
                          <th>Cubes</th>
                          <th>Chemicals</th>
                          <th>Plant</th>
                          <th>Created By</th>
                          <th>Created At</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pours.map((pour, index) => (
                          <tr key={pour._id}>
                            <td><strong>{index + 1}</strong></td>
                            <td>
                              <span className={`test-type-badge ${
                                pour.testType === 'Concrete Pour' ? 'test-type-concrete' :
                                pour.testType === 'Grout' ? 'test-type-grout' : 'test-type-trail'
                              }`}>
                                {pour.testType || 'Concrete Pour'}
                              </span>
                            </td>
                            <td><strong>{formatDate(pour.castingDate)}</strong></td>
                            <td>{getStationName(pour)}</td>
                            <td>{pour.location}</td>
                            <td><strong>{pour.mix}</strong></td>
                            <td><strong>{pour.quantity} m³</strong></td>
                            <td>{calculateCubes(pour.cubesCount)}</td>
                            <td>{pour.chemistry || '-'}</td>
                            <td>{pour.batchPlant}</td>
                            <td>
                              <div className="created-by">
                                <span className="user-name">{pour.createdBy?.fullName || '-'}</span>
                                <span className="user-role">{pour.createdBy?.role || ''}</span>
                                <span className="user-station">{pour.createdBy?.station || ''}</span>
                              </div>
                            </td>
                            <td>{formatDateTime(pour.createdAt)}</td>
                            <td>
                              <span className="status-badge">
                                <span>✓</span>
                                Approved
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards View */}
                  <div className="mobile-cards">
                    {pours.map((pour, index) => (
                      <div key={pour._id} className="pour-card-mobile">
                        <div className="card-header-mobile">
                          <span className="card-number">#{index + 1}</span>
                          <span className="status-badge">
                            <span>✓</span>
                            Approved
                          </span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Type:</span>
                          <span className="card-value">
                            <span className={`test-type-badge ${
                              pour.testType === 'Concrete Pour' ? 'test-type-concrete' :
                              pour.testType === 'Grout' ? 'test-type-grout' : 'test-type-trail'
                            }`}>
                              {pour.testType || 'Concrete Pour'}
                            </span>
                          </span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Date:</span>
                          <span className="card-value">{formatDate(pour.castingDate)}</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Station:</span>
                          <span className="card-value">{getStationName(pour)}</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Element:</span>
                          <span className="card-value">{pour.location}</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Mix Code:</span>
                          <span className="card-value">{pour.mix}</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Quantity:</span>
                          <span className="card-value">{pour.quantity} m³</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Cubes:</span>
                          <span className="card-value">{calculateCubes(pour.cubesCount)}</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Chemicals:</span>
                          <span className="card-value">{pour.chemistry || '-'}</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Plant:</span>
                          <span className="card-value">{pour.batchPlant}</span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Created By:</span>
                          <span className="card-value">
                            {pour.createdBy?.fullName || '-'} ({pour.createdBy?.role || ''})
                          </span>
                        </div>

                        <div className="card-row">
                          <span className="card-label">Created At:</span>
                          <span className="card-value">{formatDateTime(pour.createdAt)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PoursApproved;